import type { AdminConfigItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigValueEditor } from './ConfigValueEditor';
import { formatValue } from '../_lib/configDisplay';
import { FX_KEY } from '../_lib/configForms';
import type { UseConfigRowEditor } from '../_lib/useConfigRowEditor';

/**
 * The value cell of a config row (AUT-131) — display OR the type-specific
 * editor depending on edit state. Extracted from `ConfigRow` to keep its
 * render under the function-line cap.
 *
 * 🟠 #2 An absent (`present:false`) key renders "не задано" and is NEVER
 *      editable — the wire `type` derives to `'json'` for null, so we must
 *      not hand it to an editor.
 */
export interface ConfigValueCellProps {
  item: AdminConfigItemDto;
  editor: UseConfigRowEditor;
  busy: boolean;
}

export const ConfigValueCell = ({ item, editor, busy }: ConfigValueCellProps): ReactElement => {
  const { t } = useTranslation();

  if (!item.present) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {t('config.value.unset')}
      </Typography>
    );
  }

  if (editor.editing) {
    return (
      <ConfigValueEditor
        type={item.type}
        draft={editor.draft}
        onDraftChange={editor.setDraft}
        boolDraft={editor.boolDraft}
        onBoolChange={editor.setBoolDraft}
        error={editor.error}
        onSubmit={() => void editor.save()}
        disabled={busy}
      />
    );
  }

  // AUT-542 — a JSON `null` rate is «Курс не задан», not `null`.
  if (item.key === FX_KEY && item.value === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('config.fx.unset')}
      </Typography>
    );
  }

  if (item.type === 'boolean') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Switch checked={item.value === true} disabled size="small" />
        <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {item.value === true ? t('config.value.true') : t('config.value.false')}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
      {formatValue(item.value)}
    </Typography>
  );
};
